import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import type { QuestionOption } from "@/types/assessment";
import { CheckIcon, XIcon } from "lucide-react";

interface MultiSelectInputProps {
  options: QuestionOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  // After submission, show which answers were correct
  showResults?: boolean;
  correctOptionIds?: string[];
  selectedOptionIds?: string[];
  // Per-option feedback from hidden_answer (optionId -> feedback text)
  optionFeedback?: Record<string, string>;
}

export function MultiSelectInput({
  options,
  value,
  onChange,
  disabled = false,
  showResults = false,
  correctOptionIds = [],
  selectedOptionIds = [],
  optionFeedback = {},
}: MultiSelectInputProps) {
  const handleToggle = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, optionId]);
    } else {
      onChange(value.filter((id) => id !== optionId));
    }
  };

  return (
    <div className="space-y-2">
      {options.map((option) => {
        const optionId = String(option.id);
        const isChecked = value.includes(optionId);
        const wasSelected = selectedOptionIds.includes(optionId);
        const shouldBeSelected = correctOptionIds.includes(optionId);

        // Determine result state
        let resultState: "correct" | "incorrect" | "missed" | null = null;
        if (showResults) {
          if (wasSelected && shouldBeSelected) {
            resultState = "correct"; // Correctly selected
          } else if (wasSelected && !shouldBeSelected) {
            resultState = "incorrect"; // Incorrectly selected
          } else if (!wasSelected && shouldBeSelected) {
            resultState = "missed"; // Should have been selected
          }
        }

        const feedback = optionFeedback[optionId];
        const showFeedback = showResults &&
          (resultState === "incorrect" || resultState === "missed") &&
          feedback;

        return (
          <label
            key={optionId}
            className={cn(
              "block p-4 rounded-lg border cursor-pointer transition-all",
              "hover:bg-accent/50",
              disabled && "cursor-not-allowed opacity-60",
              // Selected state (before submission)
              !showResults && isChecked && "border-primary bg-accent/30",
              // After submission states
              resultState === "correct" && "border-green-500 bg-green-50 dark:bg-green-950/30",
              resultState === "incorrect" && "border-red-500 bg-red-50 dark:bg-red-950/30",
              resultState === "missed" && "border-amber-500 bg-amber-50 dark:bg-amber-950/30"
            )}
          >
            <div className="flex items-center gap-3">
              <Checkbox
                checked={showResults ? wasSelected : isChecked}
                onCheckedChange={(checked) =>
                  handleToggle(optionId, checked === true)
                }
                disabled={disabled}
                id={optionId}
              />
              <span className="flex-1">{option.content}</span>
              {showResults && (
                <span className="flex-shrink-0">
                  {resultState === "correct" && (
                    <CheckIcon className="size-5 text-green-600" />
                  )}
                  {resultState === "incorrect" && (
                    <XIcon className="size-5 text-red-600" />
                  )}
                  {resultState === "missed" && (
                    <span className="text-xs text-amber-600 font-medium">
                      Missed
                    </span>
                  )}
                </span>
              )}
            </div>
            {showFeedback && (
              <p className="mt-3 pt-3 border-t border-border/50 ml-7">
                {feedback}
              </p>
            )}
          </label>
        );
      })}
    </div>
  );
}
